import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@ordering/utils/styling';
import { ResponsiveModalContent, ResponsiveModalTitle } from './responsive-modal';

const defaultVariants = { layout: 'handle' } as const;
const drawerWrapper = cva('flex flex-col overflow-clip', {
  variants: {
    layout: {
      handle: '',
      bare: 'border-0',
    },
  },
  defaultVariants,
});

const drawerTitleRow = cva('flex items-center gap-2', {
  variants: {
    layout: {
      handle: 'px-4 mt-4',
      bare: 'sr-only',
    },
  },
  defaultVariants,
});

const drawerContent = cva('flex-1 overflow-y-auto text-sm', {
  variants: {
    layout: {
      handle: 'mt-4 px-4 border-t border-border',
      bare: '',
    },
  },
  defaultVariants,
});

export interface DrawerLayoutProps
  extends React.ComponentPropsWithoutRef<typeof ResponsiveModalContent>,
    VariantProps<typeof drawerWrapper> {
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  headerLeft?: React.ReactNode;
  dynamicHeight?: boolean;
  isNested?: boolean;
  dismissible?: boolean;
  contentClassName?: string;
  titleClassName?: string;
  footerClassName?: string;
}

const DrawerLayout = React.forwardRef<React.ComponentRef<typeof ResponsiveModalContent>, DrawerLayoutProps>(
  (
    {
      title,
      children,
      footer,
      headerLeft,
      dynamicHeight = false,
      layout,
      isNested = false,
      dismissible = true,
      contentClassName,
      titleClassName,
      footerClassName,
      className,
      ...props
    },
    ref
  ) => {
    const effectiveLayout = layout ?? defaultVariants.layout;

    return (
      <ResponsiveModalContent
        ref={ref}
        isNested={isNested}
        className={cn(drawerWrapper({ layout, className }), !dynamicHeight && 'h-full', dynamicHeight && 'max-h-[80dvh]')}
        displayHandle={effectiveLayout === 'handle' && dismissible}
        {...props}
      >
        <div className={cn(drawerTitleRow({ layout }), titleClassName)}>
          {headerLeft}
          <ResponsiveModalTitle className="flex-1">{title}</ResponsiveModalTitle>
        </div>
        <div className={cn(drawerContent({ layout }), dynamicHeight && 'min-h-0 overflow-y-auto pb-4', contentClassName)}>{children}</div>
        {footer && <div className={cn('px-4 py-6 border-t border-border', footerClassName)}>{footer}</div>}
      </ResponsiveModalContent>
    );
  }
);
DrawerLayout.displayName = 'DrawerLayout';

export { DrawerLayout };
